import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatchReportComponent } from './match-report/match-report.component';
import { QualificationComponent } from './qualification/qualification.component';
import { JobDescriptionComponent } from './job-description/job-description.component';
import { Store } from '@ngrx/store';
import { AdaptIDJobPostingSchema, JobListSchema } from '@app/schemas/jobs';
import { TimeAgoPipe } from '@app/utils/time-difference.pipe';
import { UnderscoreToLowerPipe } from '@app/utils/string-transform.pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import { analytics } from '@app/events/segment/analytics';
import { setJobList } from '@app/state/actions/jobs.action';
import { NgClass } from '@angular/common';
import { UserJobSearchService } from '@app/services/api/job-search/user-job-search.service';
import { WarningSnackBarComponent } from '@app/shared/snack-bars/warning/warning.component';
import { WarningDefaultOptions } from '@app/shared/snack-bars/warning/warning.utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserJobBookmarkService } from '@app/services/api/job-bookmark/user-job-bookmark.service';
import { finalize, take } from 'rxjs';
import { updateLoaderState } from '@app/state/actions/global.action';

@Component({
  selector: 'app-job-description-module',
  imports: [
    MatchReportComponent,
    QualificationComponent,
    JobDescriptionComponent,
    TimeAgoPipe,
    UnderscoreToLowerPipe,
    MatTooltipModule,
    NgClass
  ],
  templateUrl: './job-description-module.component.html',
  styleUrl: './job-description-module.component.css'
})
export class JobDescriptionModuleComponent implements OnChanges {
  @Input() selectedJobId: string = '';
  @Output() jobCloseEmitter = new EventEmitter<void>();
  public showJobDescription: boolean = false;
  public selectedJob: any = {};
  public jobListData: JobListSchema[] = [];
  private _snackBar = inject(MatSnackBar);
  public isBookmarkDisable: boolean = false;
  public isRemoteJob: boolean = false;

  constructor(
    private store: Store<{ 
      jobList: JobListSchema[], 
      adaptIdJobList: AdaptIDJobPostingSchema[],
     }>,
    private userJobSearchService: UserJobSearchService,
    private jobBookmarkService: UserJobBookmarkService
    ) {
     this.store
      .select('jobList')
      .subscribe((list: JobListSchema[]) => {
        this.jobListData = list || [];
        this.getJobById();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedJobId']) {
      this.showJobDescription = !!changes['selectedJobId'].currentValue;
      this.getJobById();
    }
  }

  getJobById() {
    if (!this.selectedJobId) {
      this.selectedJob = {};
      this.showJobDescription = false;
      return;
    }

    const job = this.jobListData.find(j => j.posting_id === this.selectedJobId);
    if (job) {
      this.selectedJob = { ...job };
      this.isRemoteJob = this.selectedJob.employment_location_type?.includes('REMOTE') || false;
    } else {
      this.selectedJob = {};
      this.showJobDescription = false;
    }
  }

  closeJobDescription() {
    this.showJobDescription = false;
    this.jobCloseEmitter.emit();
  }

  showErrorMessage(msg: string) {
    this._snackBar.openFromComponent(WarningSnackBarComponent, {
      ...WarningDefaultOptions,
      data: {
        message: msg,
      },
      duration: 3000,
    });
  }
 
  onClickJobBookmark(jobId: string, status: 'ADD' | 'REMOVE') {
    this.isBookmarkDisable = true;
    let adaptIDJobs: AdaptIDJobPostingSchema[] = [];
    this.store.select('adaptIdJobList').pipe(take(1)).subscribe(value => adaptIDJobs = value);


    const jobPosting = adaptIDJobs.find(
      jobPosting => jobPosting.job.posting_id === jobId
    );

    if (!jobPosting) {
      console.error('Job posting not found for ID:', jobId);
      this.isBookmarkDisable = false;
      return;
    }

    this.jobBookmarkService.addRemoveBookmarkedJob(jobPosting, status).pipe(
      finalize(() => this.isBookmarkDisable = false)
    ).subscribe({
      next: (data) => {
        this.selectedJob.is_bookmarked = status === 'ADD';
        
        const updatedList = this.jobListData.map((job: JobListSchema) =>
          this.selectedJob.posting_id === job.posting_id ? { ...job, is_bookmarked: status === 'ADD' } : job
        );
        this.store.dispatch(setJobList({ jobs: updatedList }));
        this.trackBookmarkClick(status === 'ADD');
      },
      error: (error) => {
        console.error('Error getting user jobs:', error);
        this.showErrorMessage('Error while updating bookmark. Please try again!');
      }
    });
  }

  trackBookmarkClick(value: boolean) {
    const eventName = value ? 'Job Details Bookmark Check' : 'Job Details Bookmark Uncheck';
    analytics.track(eventName , {
      section: 'job',
      job_id: this.selectedJob.posting_id,
      job_title: this.selectedJob.job_title,
      employer_name: this.selectedJob.employer_name || null,
    })
  }

  trackApplyClick() {
    // Track Apply button click event
    analytics.track('Job Details Apply', {
      section: 'job',
      job_id: this.selectedJob.posting_id,
      job_title: this.selectedJob.job_title,
      employer_name: this.selectedJob.employer_name || null,
    });
  }
}
